import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useUsers } from '../../context/UserManagementContext';
import { Dropdown } from '@silverfort/sf-components';
import { FilterDetails, UsersFilterKeys, UsersFilters, UsersFilterValue } from '../../types/filters';
import { useGetDomains } from '../../api';
import { DOMAIN_FILTER_ID, getUsersTableBaseFilters, GROUP_FILTER_ID } from './utils';
import { getDropdownOptionsFromFilterOptions, getSortedFiltersDetails, isAccountMatchingEnabled } from '../../utils';
import { ProtectedEntityType } from '../../../../api';
import { setFilterValues as setFilterValuesFactory } from '../../utils';
import { AdGroup } from '../../types/AdGroup';

interface FilterItemProps {
    filterValue?: UsersFilterValue;
    filterDetails: FilterDetails<UsersFilters>;
    onFilterChange: (filterId: UsersFilterKeys, selectedValues?: UsersFilterValue) => void;
}

const FilterItem: FC<FilterItemProps> = ({ filterValue, filterDetails, onFilterChange }) => (
    <Dropdown
        variant="filter"
        multiple={filterDetails.multiple}
        searchable={filterDetails.searchable}
        title={filterDetails.label}
        selectedItems={filterValue}
        removable={Array.isArray(filterValue) ? !!filterValue?.length : !!filterValue}
        onRemove={() => onFilterChange(filterDetails.id, undefined)}
        items={getDropdownOptionsFromFilterOptions(filterDetails.options)}
        onChange={(selectedValues) => onFilterChange(filterDetails.id, selectedValues)}
        width="232px"
        searchPlaceholder={filterDetails.searchPlaceholder}
    />
);

const reduceFiltersData = (filterId: UsersFilterKeys, filterValues?: UsersFilterValue) => {
    return ({ [filterId]: prevFilterValue, ...prevFilters }) => {
        if (filterValues) {
            return {
                ...prevFilters,
                [filterId]: filterValues
            } as UsersFilters;
        }

        return prevFilters as UsersFilters;
    };
};

const UsersTableFilters = () => {
    const { filters, setFilters, systemData, savedProtectedEntities } = useUsers();
    const { domains } = useGetDomains();
    const [filtersDetails, setFiltersDetails] = useState<FilterDetails<UsersFilters>[]>([]);
    const [filtersData, setFiltersData] = useState<Partial<UsersFilters>>({});

    const hasAccountMatching = useMemo(() => isAccountMatchingEnabled(systemData), [systemData]);

    const protectedEntities = useMemo(() => {
        return savedProtectedEntities
            .filter((entity) => entity.type === ProtectedEntityType.GROUP || entity.type === ProtectedEntityType.OU)
            .map((entity) => ({ guid: entity.guid, name: entity.name, domainName: entity.domainName } as AdGroup));
    }, [savedProtectedEntities]);

    useEffect(() => {
        setFiltersDetails(
            getUsersTableBaseFilters({
                hasAccountMatching,
                domains: domains ?? [],
                groups: protectedEntities
            })
        );
    }, [domains, protectedEntities, hasAccountMatching]);

    const setFilterValues = useMemo(() => setFilterValuesFactory(setFilters), [setFilters]);

    const filterAllValuesFiltersData = useCallback(
        (filterId: UsersFilterKeys, filterValues?: UsersFilterValue, allCount?: number) => {
            if (Array.isArray(filterValues) && filterValues.length === allCount) {
                setFiltersData(reduceFiltersData(filterId, filterValues));
                return [];
            }
            setFiltersData(reduceFiltersData(filterId, undefined));
            return filterValues;
        },
        [setFiltersData]
    );

    const setFiltersValues = useCallback(
        (filterId: UsersFilterKeys, filterValues?: UsersFilterValue) => {
            let filterValuesToSet: UsersFilterValue | undefined = filterValues;
            if (filterId === DOMAIN_FILTER_ID) {
                filterValuesToSet = filterAllValuesFiltersData(filterId, filterValues, domains?.length);
            } else if (filterId === GROUP_FILTER_ID) {
                filterValuesToSet = filterAllValuesFiltersData(filterId, filterValues, protectedEntities.length);
            }

            setFilterValues(filterId, filterValuesToSet);
        },
        [setFilterValues, domains, protectedEntities, filterAllValuesFiltersData]
    );

    const sortedFilters = useMemo(
        () => getSortedFiltersDetails({ ...filters, ...filtersData }, filtersDetails),
        [filters, filtersData, filtersDetails]
    );

    return (
        <div className="flex gap-2">
            {sortedFilters.map((filter) => (
                <FilterItem
                    key={filter.id}
                    filterValue={filtersData[filter.id] || filters[filter.id]}
                    filterDetails={filter}
                    onFilterChange={setFiltersValues}
                />
            ))}
        </div>
    );
};

export default UsersTableFilters;
